export function formatMoney(minorUnits: number, currency: string, locale = 'ru-RU'): string {
  if (!Number.isSafeInteger(minorUnits)) return '—';
  try {
    const formatter = new Intl.NumberFormat(locale, { style: 'currency', currency });
    const digits = formatter.resolvedOptions().maximumFractionDigits ?? 2;
    const magnitude = BigInt(Math.abs(minorUnits));
    const divisor = 10n ** BigInt(digits);
    const whole = magnitude / divisor;
    const value = minorUnits < 0 ? (whole === 0n ? -0 : -whole) : whole;
    const fraction = String(magnitude % divisor).padStart(digits, '0');
    return formatter
      .formatToParts(value)
      .map((part) => (part.type === 'fraction' ? fraction : part.type === 'minusSign' ? '−' : part.value))
      .join('');
  } catch {
    return '—';
  }
}

export function parseInstant(value: string | Date): Date | null {
  const date = value instanceof Date ? value : new Date(value);
  return Number.isFinite(date.getTime()) ? date : null;
}

export function formatDateTime(value: string | Date, timeZone: string): string {
  const date = parseInstant(value);
  if (!date) return 'Дата не указана';
  try {
    return new Intl.DateTimeFormat('ru-RU', {
      timeZone,
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  } catch {
    return 'Часовой пояс недоступен';
  }
}

function calendarDay(value: Date, timeZone: string): number {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(value);
  const part = (key: string) => Number(parts.find((p) => p.type === key)?.value);
  return Date.UTC(part('year'), part('month') - 1, part('day')) / 86400000;
}

export function deadlineLabel(
  value: string | Date,
  now: number,
  timeZone: string,
): { label: string; overdue: boolean } {
  const date = parseInstant(value);
  if (!date || !Number.isFinite(now)) return { label: 'Срок не указан', overdue: false };
  if (date.getTime() < now) return { label: 'Просрочено', overdue: true };
  try {
    const days = calendarDay(date, timeZone) - calendarDay(new Date(now), timeZone);
    if (days === 0) return { label: 'Сегодня', overdue: false };
    if (days === 1) return { label: 'Завтра', overdue: false };
    const word = { one: 'день', few: 'дня', many: 'дней', other: 'дня', zero: 'дней', two: 'дня' }[
      new Intl.PluralRules('ru-RU').select(days)
    ];
    return { label: `Через ${days} ${word}`, overdue: false };
  } catch {
    return { label: 'Срок недоступен', overdue: false };
  }
}
